import React, { useEffect, useState } from "react";
import { Route, Switch } from "react-router";
import { useParams } from "react-router-dom";
import { lazyWithRetry } from "../core/lazy_with_retry";
import { AuthPortalCustomLoginProvider } from "./auth_portal_custom_login_provider";
import { PartnerConfig } from "./partner_config";
import { PortalHomePage } from "./portal/pages/portal_home_page";
import { AppTracker } from "../core/app_tracker";
import { usePartnerQuery } from "./api";
import { PortalMembershipPage } from "./portal/pages/portal_membership_page";
import { PortalMeetingRoomPage } from "./portal/pages/portal_meeting_room_page";
import { PortalManageMembershipPage } from "./portal/pages/portal_manage_membership_page";
import { getPortalPaths } from "./portal/hooks/use_portal_history";
import { PortalAllBookingsPage } from "./portal/pages/portal_all_bookings_page";
import { useAnalytics } from "@/lib/analytics/analytics";
import { PortalLoginPage } from "./portal/pages/portal_login_page";
import { PortalCallbackPage } from "./portal_callback_page";
import { AuthPortalProvider } from "./auth_portal_provider";
import { AuthPortalSWRClient } from "./auth_swr_client";
import { useQueryString } from "@/hooks";
import { portalAuth } from "./portal_auth_config";
import { useConfig } from "@/core/config";
import { PortalSignupPage } from "./portal/pages/portal_signup_page";

interface PortalPagesWrapperParams {
  partnerSlug: string;
}

export default function PortalPagesWrapper() {
  const params = useParams<PortalPagesWrapperParams>();
  const query = useQueryString();
  const config = useConfig();
  const partnerSlug =
    query.partnerSlug ||
    localStorage.getItem("partnerSlug") ||
    params.partnerSlug;

  const partner = usePartnerQuery(partnerSlug);
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.register({ Source: "Portal" });
  }, [analytics]);

  const [partnerConfig, setPartnerConfig] = useState<PartnerConfig>();
  import(`./${partnerSlug}/${partnerSlug}_partner_config.tsx`).then(
    (partnerConfig) => {
      setPartnerConfig(partnerConfig.partnerConfig);
    }
  );

  useEffect(() => {
    if (partner?.name) {
      analytics.register({ "Partner Name": partner.name });
    }
  }, [analytics, partner?.name]);

  const portalAuthConfigs = portalAuth(partnerSlug);
  const currentPortalAuthConfig = portalAuthConfigs[config.environment];

  if (!partnerConfig || !currentPortalAuthConfig) {
    return null;
  }

  const Theme = lazyWithRetry(
    () => import(`./${partnerSlug}/${partnerSlug}_theme.tsx`)
  );

  const portalPath = getPortalPaths();

  return (
    <AuthPortalCustomLoginProvider partnerSlug={partnerSlug}>
      <Switch>
        <Route exact path="/portal/auth/login">
          <Theme />
          <PortalLoginPage />
        </Route>
        <Route exact path="/portal/auth/signup">
          <Theme />
          <PortalSignupPage />
        </Route>
      </Switch>

      <AuthPortalProvider
        clientId={currentPortalAuthConfig.clientId}
        domain={currentPortalAuthConfig.domain}
        audience={currentPortalAuthConfig.audience}
        redirectUri={currentPortalAuthConfig.redirectUri}
      >
        <AuthPortalSWRClient>
          <Switch>
            <Route exact path="/portal/auth/callback">
              <PortalCallbackPage />
            </Route>
            <Route path={portalPath.meetingRooms}>
              <PortalMeetingRoomPage
                partnerConfig={partnerConfig}
                partner={partner}
              />
            </Route>
            <Route path={portalPath.manageMembership}>
              <PortalManageMembershipPage
                partnerConfig={partnerConfig}
                partner={partner}
              />
            </Route>
            <Route path={portalPath.membership}>
              <PortalMembershipPage
                partnerConfig={partnerConfig}
                partner={partner}
              />
            </Route>
            <Route path={portalPath.allBookings}>
              <PortalAllBookingsPage
                partnerConfig={partnerConfig}
                partner={partner}
              />
            </Route>
            <Route exact path={portalPath.home}>
              <PortalHomePage partnerConfig={partnerConfig} partner={partner} />
            </Route>
          </Switch>
          <AppTracker />
        </AuthPortalSWRClient>
      </AuthPortalProvider>
    </AuthPortalCustomLoginProvider>
  );
}
